import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { api, throwIfNotOk } from '../lib/api';
import { addDaysISO, addMonthsISO, endOfDayISO } from '../lib/dates';
import { DateField, DateRangeField } from '../components/shared/DateField';
import { ManufacturerLogo } from '../components/shared/ManufacturerLogo';
import { conditionLabel } from '@siliconx/shared/ui/ConditionBadge';
import type { CatalogBucket, ConditionBand, Reach } from '@siliconx/shared/dto/direct-offer';
import { qk } from '../lib/queryKeys';
import { Button } from '@siliconx/shared/ui/Button';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { TextInput } from '@siliconx/shared/ui/Field';
import { requireOrgFeature } from '../lib/orgFeatureGate';

// SIL-171: the flat SKU list (RUF-550) is replaced by the anonymized
// (SKU, grade, region) direct-offer catalog (SIL-170's GET /catalog).
// Browsing stays ungated; submitting a direct offer still requires canBuy
// (enforced server-side — the drawer surfaces the 403 via toast if it slips
// through).
//
// Browse Inventory redesign (claude.ai/design comp): buckets are grouped
// client-side into manufacturer × asset-class sections of per-SKU cards
// (condition-mix bar, FMV median), and the submit dialog becomes a slide-in
// drawer with condition/region pill targeting and a live seller-reach
// callout. The comp's 30d trend chip, low/high FMV band labels, expiry date,
// and multi-condition targeting are omitted: the catalog deliberately
// exposes no FMV internals beyond mcmp_usd, and a direct_offer targets
// exactly one (sku, condition_band, region) bucket.
// SIL-1108: `orgFeature: 'marketplaceOps'` (customerNav.ts) is
// business-only — gated here so a direct hit doesn't fire this page's
// mount-time fetch before the root `OrgFeatureGate`'s (__root.tsx) redirect
// lands.
export const Route = createFileRoute('/inventory')({
  beforeLoad: () => requireOrgFeature('marketplaceOps'),
  component: InventoryPage,
});

interface CatalogResponse {
  data: CatalogBucket[];
  meta: { nextCursor: string | null; limit: number };
}

interface ReferenceResponse {
  assetClasses: { id: string; name: string }[];
  regions: { id: string; name: string }[];
}

const CONDITION_OPTIONS = [
  { value: '', label: 'Any condition' },
  { value: 'new', label: 'New' },
  { value: 'like_new', label: 'Like new' },
  { value: 'normal_enterprise_wear', label: 'Normal wear' },
  { value: 'high_wear', label: 'High wear' },
  { value: 'unknown', label: 'Unknown' },
] as const;

// Band display order + segment-bar colors for the per-card condition mix.
const BAND_ORDER: ConditionBand[] = ['new', 'like_new', 'normal_enterprise_wear', 'high_wear', 'unknown'];
const BAND_BAR: Record<string, string> = {
  new: 'bg-violet-500',
  like_new: 'bg-emerald-500',
  normal_enterprise_wear: 'bg-sky-500',
  high_wear: 'bg-amber-500',
  unknown: 'bg-muted-foreground',
};

function usd(n: number): string {
  return '$' + Math.round(n).toLocaleString('en-US');
}

async function fetchCatalog(params: {
  assetClass?: string;
  condition?: string;
  regionId?: string;
}): Promise<CatalogResponse> {
  // ponytail: single page, limit 200 — follow the cursor if catalogs outgrow it
  const query: Record<string, string> = { limit: '200' };
  if (params.assetClass) query.assetClass = params.assetClass;
  if (params.condition) query.condition = params.condition;
  if (params.regionId) query.regionId = params.regionId;
  const res = await api.api.client.catalog.$get({ query });
  await throwIfNotOk(res);
  return (await res.json()) as unknown as CatalogResponse;
}

// ---------------------------------------------------------------------------
// Grouping: catalog buckets → per-SKU groups → (manufacturer × class) sections
// ---------------------------------------------------------------------------

interface SkuGroup {
  skuId: string;
  modelName: string;
  make: string;
  manufacturerId: string | null;
  assetClassId: string;
  buckets: CatalogBucket[];
  totalUnits: number;
  perBand: Record<string, number>;
  /** MCMP is per (sku, band) — representative price = band with most units. */
  mcmpUsd: number;
}

/** Model without a duplicated manufacturer prefix — the header already names it. */
function cardLabel(g: SkuGroup): string {
  const m = g.modelName;
  return m.toLowerCase().startsWith(g.make.toLowerCase())
    ? m.slice(g.make.length).trim() || m
    : m;
}

interface MfrGroup {
  make: string;
  manufacturerId: string | null;
  groups: SkuGroup[];
  totalUnits: number;
}

interface Section {
  assetClass: string;
  mfrs: MfrGroup[];
  totalUnits: number;
  skuCount: number;
}

function buildSections(buckets: CatalogBucket[]): Section[] {
  const bySku = new Map<string, CatalogBucket[]>();
  for (const b of buckets) {
    const list = bySku.get(b.skuId) ?? [];
    list.push(b);
    bySku.set(b.skuId, list);
  }

  const groups: SkuGroup[] = [...bySku.entries()].map(([skuId, list]) => {
    const perBand: Record<string, number> = {};
    let totalUnits = 0;
    for (const b of list) {
      perBand[b.conditionBand] = (perBand[b.conditionBand] ?? 0) + b.availableQty;
      totalUnits += b.availableQty;
    }
    const topBand = BAND_ORDER.filter((k) => perBand[k]).sort(
      (a, b) => (perBand[b] ?? 0) - (perBand[a] ?? 0),
    )[0];
    const first = list[0];
    const mcmpUsd = list.find((b) => b.conditionBand === topBand)?.mcmpUsd ?? first?.mcmpUsd ?? 0;
    return {
      skuId,
      modelName: first?.modelName ?? skuId.slice(0, 8),
      make: first?.manufacturerName ?? 'Unknown manufacturer',
      manufacturerId: first?.manufacturerId ?? null,
      assetClassId: first?.assetClassId ?? 'Other',
      buckets: list,
      totalUnits,
      perBand,
      mcmpUsd,
    };
  });

  // Hierarchy: asset class → manufacturer → SKUs.
  const sections = new Map<string, Section>();
  for (const g of groups) {
    const sec = sections.get(g.assetClassId) ?? {
      assetClass: g.assetClassId,
      mfrs: [],
      totalUnits: 0,
      skuCount: 0,
    };
    let mfr = sec.mfrs.find((m) => m.make === g.make);
    if (!mfr) {
      mfr = { make: g.make, manufacturerId: g.manufacturerId, groups: [], totalUnits: 0 };
      sec.mfrs.push(mfr);
    }
    mfr.groups.push(g);
    mfr.totalUnits += g.totalUnits;
    sec.totalUnits += g.totalUnits;
    sec.skuCount += 1;
    sections.set(g.assetClassId, sec);
  }
  const out = [...sections.values()].sort((a, b) => a.assetClass.localeCompare(b.assetClass));
  for (const sec of out) sec.mfrs.sort((a, b) => a.make.localeCompare(b.make));
  return out;
}

// ---------------------------------------------------------------------------
// Offer drawer — condition/region pill targeting over the SKU's buckets,
// price + quantity, debounced live reach preview, and a submitted state.
// ---------------------------------------------------------------------------
function OfferDrawer({
  group,
  skuLabel,
  regionById,
  onClose,
}: {
  group: SkuGroup;
  skuLabel: string;
  regionById: Map<string, string>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const bands = BAND_ORDER.filter((k) => (group.perBand[k] ?? 0) > 0);
  const regions = [...new Set(group.buckets.map((b) => b.regionId).filter((r): r is string => r !== null))];

  const [band, setBand] = useState<ConditionBand>(bands[0] ?? 'unknown');
  const [region, setRegion] = useState<string | null>(null); // null = any region
  const bandMcmp = group.buckets.find((b) => b.conditionBand === band)?.mcmpUsd ?? 0;

  const [price, setPrice] = useState(String(Math.round(bandMcmp)));
  const [quantity, setQuantity] = useState('1');
  // 670: optional delivery window + expiry.
  const [deliverFrom, setDeliverFrom] = useState('');
  const [deliverBy, setDeliverBy] = useState('');
  const [expiresOn, setExpiresOn] = useState('');
  const [reach, setReach] = useState<Reach | null>(null);
  const [reachLoading, setReachLoading] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Eligible units for the current (band, region) targeting. A null-region
  // bucket (unresolved data center) only counts under "Any region".
  const eligibleUnits = group.buckets
    .filter((b) => b.conditionBand === band && (region === null || b.regionId === region))
    .reduce((a, b) => a + b.availableQty, 0);

  const bandUnits = (k: ConditionBand) =>
    group.buckets
      .filter((b) => b.conditionBand === k && (region === null || b.regionId === region))
      .reduce((a, b) => a + b.availableQty, 0);

  const priceNum = parseFloat(price);
  const quantityNum = parseInt(quantity, 10);
  const priceValid = !Number.isNaN(priceNum) && priceNum > 0;
  const quantityValid = Number.isInteger(quantityNum) && quantityNum > 0;
  const deltaPct = priceValid && bandMcmp > 0 ? ((priceNum - bandMcmp) / bandMcmp) * 100 : 0;

  // Reset price to the band's MCMP when the band changes.
  useEffect(() => {
    setPrice(bandMcmp ? String(Math.round(bandMcmp)) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [band]);

  // Debounced live reach preview — clears any pending 422 gate error since
  // the user is actively adjusting price/targeting in response to it.
  useEffect(() => {
    setGateError(null);
    if (!priceValid) {
      setReach(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setReachLoading(true);
      api.api.client.catalog.reach
        .$get({
          query: {
            skuId: group.skuId,
            condition: band,
            pricePerUnitUsd: String(priceNum),
            ...(region ? { regionId: region } : {}),
          },
        })
        .then(async (res) => {
          await throwIfNotOk(res);
          setReach((await res.json()) as unknown as Reach);
        })
        .catch(() => setReach(null))
        .finally(() => setReachLoading(false));
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceNum, priceValid, band, region]);

  const submit = useMutation({
    mutationFn: async () => {
      const res = await api.api.client['direct-offers'].$post({
        json: {
          skuId: group.skuId,
          conditionBand: band,
          regionId: region,
          quantityDesired: quantityNum,
          pricePerUnitUsd: priceNum,
          deliverFrom: deliverFrom || null,
          deliverBy: deliverBy || null,
          expiresAt: expiresOn ? endOfDayISO(expiresOn) : null,
        },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.directOffers() });
      void queryClient.invalidateQueries({ queryKey: qk.catalog.all });
      setSubmitted(true);
    },
    onError: (err: Error & { body?: { error?: { code?: string; message?: string; reach?: Reach } } }) => {
      const errBody = err.body?.error;
      if (errBody?.code === 'reaches_no_sellers') {
        setGateError(errBody.message ?? 'Reaches no sellers at this price. Raise your price/unit and try again.');
        return;
      }
      toast.error(errBody?.message ?? err.message ?? 'Failed to submit offer.');
    },
  });

  const zeroReach = reach !== null && reach.clearingSellers === 0;
  const windowValid = !deliverFrom || !deliverBy || deliverFrom <= deliverBy;
  const canSubmit = priceValid && quantityValid && windowValid && !submit.isPending && !zeroReach;
  const sellers = reach?.clearingSellers ?? null;

  const pillClass = (selected: boolean, enabled = true) =>
    [
      'rounded-full border px-3 py-1 text-xs font-medium whitespace-nowrap',
      selected
        ? 'border-brand-600 bg-brand-50 text-foreground'
        : 'border-border bg-transparent text-muted-foreground',
      enabled ? 'cursor-pointer hover:border-brand-400' : 'cursor-default opacity-45',
    ].join(' ');

  // Marker position on the vs-median meter: median centered, ±30% span.
  const markerPct = Math.max(2, Math.min(98, 50 + (deltaPct / 30) * 48));

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-black/40"
      />
      <div className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col overflow-y-auto border-l border-border bg-card shadow-xl">
        {!submitted ? (
          <div className="flex flex-1 flex-col gap-5 p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Submit Offer
                </div>
                <h2 className="mt-1 text-lg font-semibold text-foreground">{skuLabel}</h2>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  FMV median {usd(bandMcmp)} &middot; {conditionLabel(band)}
                </div>
              </div>
              <button
                type="button"
                aria-label="Close drawer"
                onClick={onClose}
                className="rounded-md p-1 text-muted-foreground hover:text-foreground"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </div>

            {/* Condition targeting */}
            <div>
              <div className="mb-1.5 text-xs text-muted-foreground">Condition</div>
              <div className="flex flex-wrap gap-1.5">
                {bands.map((k) => (
                  <button key={k} type="button" onClick={() => setBand(k)} className={pillClass(band === k)}>
                    {conditionLabel(k)} &middot; {bandUnits(k)}
                  </button>
                ))}
              </div>
            </div>

            {/* Region targeting */}
            <div>
              <div className="mb-1.5 text-xs text-muted-foreground">Region</div>
              <div className="flex flex-wrap gap-1.5">
                <button type="button" onClick={() => setRegion(null)} className={pillClass(region === null)}>
                  Any region
                </button>
                {regions.map((r) => (
                  <button key={r} type="button" onClick={() => setRegion(r)} className={pillClass(region === r)}>
                    {regionById.get(r) ?? r}
                  </button>
                ))}
              </div>
            </div>

            {/* Reach callout */}
            <div className="flex items-center gap-2.5 rounded-lg border border-brand-200 bg-brand-50 px-3.5 py-3 text-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-brand-600">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              <div>
                <span className="font-semibold text-foreground">
                  {eligibleUnits} unit{eligibleUnits === 1 ? '' : 's'}
                </span>{' '}
                <span className="text-muted-foreground">
                  {reachLoading && 'checking seller reach…'}
                  {!reachLoading && reach && (
                    <>
                      &middot; reaches {reach.clearingSellers} of {reach.totalSellers} seller
                      {reach.totalSellers === 1 ? '' : 's'} at this price
                    </>
                  )}
                  {!reachLoading && !reach && 'across eligible sellers'}
                </span>
              </div>
            </div>

            {/* Price + quantity */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="offer-price" className="mb-1 block text-xs text-muted-foreground">
                  Price per unit (USD)
                </label>
                <TextInput mono className="text-foreground"
                  id="offer-price"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="offer-quantity" className="mb-1 block text-xs text-muted-foreground">
                  Quantity
                </label>
                <TextInput mono className="text-foreground"
                  id="offer-quantity"
                  type="number"
                  min="1"
                  step="1"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
            </div>

            {/* vs FMV median meter */}
            <div>
              <div className="mb-1.5 flex justify-between text-xs text-muted-foreground">
                <span>vs FMV median</span>
                <span className="font-mono">
                  {deltaPct >= 0 ? '+' : '−'}
                  {Math.abs(deltaPct).toFixed(1)}%
                </span>
              </div>
              <div className="relative h-1.5 rounded-full bg-muted">
                <div className="absolute inset-y-0 left-[18%] right-[18%] rounded-full bg-brand-100" />
                <div
                  className="absolute -top-1 h-3.5 w-0.5 rounded-sm bg-brand-600"
                  style={{ left: `${markerPct}%` }}
                />
              </div>
              <div className="mt-1 flex justify-between font-mono text-[13px] text-muted-foreground">
                <span>−30%</span>
                <span>{usd(bandMcmp)} med</span>
                <span>+30%</span>
              </div>
            </div>

            {/* Delivery window + expiry (670) */}
            <div className="space-y-3">
              <DateRangeField
                fromLabel="Deliver from"
                byLabel="Deliver by"
                from={deliverFrom}
                by={deliverBy}
                onFromChange={setDeliverFrom}
                onByChange={setDeliverBy}
                min={addDaysISO(1)}
                max={addMonthsISO(12)}
                helper="Optional — when you want ownership to transfer. Sellers see this window."
              />
              <DateField
                label="Expiration"
                value={expiresOn}
                onChange={setExpiresOn}
                min={addDaysISO(1)}
                helper="Optional — the offer expires at the end of this day if unfilled."
              />
            </div>

            {gateError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-2.5 text-xs text-red-700">{gateError}</div>
            )}

            {/* Submit */}
            <div className="mt-auto flex flex-col gap-2 pt-2">
              <Button className="disabled:bg-muted disabled:cursor-default disabled:text-muted-foreground font-semibold py-2.5 w-full"
                onClick={() => submit.mutate()}
                disabled={!canSubmit}
              >
                {submit.isPending
                  ? 'Submitting…'
                  : zeroReach
                    ? 'No sellers clear this price'
                    : sellers !== null
                      ? `Submit Offer to ${sellers} seller${sellers === 1 ? '' : 's'}`
                      : 'Submit Offer'}
              </Button>
              <div className="text-center text-xs text-muted-foreground">
                Sellers are notified anonymously. Withdraw any time before it fills.
              </div>
            </div>
          </div>
        ) : (
          /* Submitted confirmation */
          <div className="flex flex-1 flex-col items-center justify-center gap-5 p-6 text-center">
            <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" className="text-brand-600">
              <circle cx="12" cy="12" r="10" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            <div>
              <div className="text-lg font-semibold text-foreground">Offer submitted</div>
              <div className="mt-1 text-sm text-muted-foreground">Eligible sellers have been notified anonymously.</div>
            </div>
            <dl className="w-full rounded-lg border border-border bg-muted p-4 text-left text-sm">
              {(
                [
                  ['SKU', skuLabel],
                  ['Targets', `${conditionLabel(band)} · ${region ? (regionById.get(region) ?? region) : 'Any region'}`],
                  ['Price per unit', usd(priceNum)],
                  ['Quantity', String(quantityNum)],
                ] as const
              ).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-1">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="text-right font-medium text-foreground">{v}</dd>
                </div>
              ))}
            </dl>
            <div className="flex w-full flex-col gap-2">
              <Button className="font-semibold py-2.5 w-full"
                onClick={onClose}
              >
                Done
              </Button>
              <div className="text-xs text-muted-foreground">
                Track responses under{' '}
                <Link to="/offers" className="text-brand-600 hover:underline">
                  Offers
                </Link>
                .
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
function InventoryPage() {
  const [q, setQ] = useState('');
  const [assetClass, setAssetClass] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [condition, setCondition] = useState('');
  const [regionId, setRegionId] = useState('');
  const [drawerSkuId, setDrawerSkuId] = useState<string | null>(null);

  const catalogQ = useQuery({
    queryKey: qk.catalog.list(assetClass, condition, regionId),
    queryFn: () =>
      fetchCatalog({
        assetClass: assetClass || undefined,
        condition: condition || undefined,
        regionId: regionId || undefined,
      }),
  });
  const data = catalogQ.data;

  const { data: reference } = useQuery({
    queryKey: qk.reference(),
    queryFn: async () => {
      const res = await api.api.client.reference.$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as ReferenceResponse;
    },
    staleTime: 5 * 60_000,
  });

  const regionById = useMemo(
    () => new Map((reference?.regions ?? []).map((r) => [r.id, r.name])),
    [reference],
  );
  const classById = useMemo(
    () => new Map((reference?.assetClasses ?? []).map((ac) => [ac.id, ac.name])),
    [reference],
  );

  const sections = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return buildSections(data?.data ?? [])
      .map((s) => ({
        ...s,
        mfrs: s.mfrs
          .filter((m) => !manufacturer || m.make === manufacturer)
          .map((m) => ({
            ...m,
            groups: needle
              ? m.groups.filter((g) =>
                  `${m.make} ${g.modelName} ${s.assetClass}`.toLowerCase().includes(needle),
                )
              : m.groups,
          }))
          .filter((m) => m.groups.length > 0)
          .map((m) => ({ ...m, totalUnits: m.groups.reduce((a, g) => a + g.totalUnits, 0) })),
      }))
      .filter((s) => s.mfrs.length > 0)
      .map((s) => ({
        ...s,
        totalUnits: s.mfrs.reduce((a, m) => a + m.totalUnits, 0),
        skuCount: s.mfrs.reduce((a, m) => a + m.groups.length, 0),
      }));
  }, [data, manufacturer, q]);

  const manufacturers = useMemo(
    () =>
      [
        ...new Set(buildSections(data?.data ?? []).flatMap((s) => s.mfrs.map((m) => m.make))),
      ].sort(),
    [data],
  );

  const unitsShown = sections.reduce((a, s) => a + s.totalUnits, 0);
  const skusShown = sections.reduce((a, s) => a + s.skuCount, 0);

  const drawerGroup = drawerSkuId
    ? sections
        .flatMap((s) => s.mfrs)
        .flatMap((m) => m.groups)
        .find((g) => g.skuId === drawerSkuId)
    : null;

  const selectClass =
    'rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-500';

  return (
    <div className="mx-auto max-w-6xl">
      {/* Header */}
      <div className="mb-1.5 flex items-start justify-between gap-4">
        <div>
          <PageHeader title="Market Inventory" pinTo="/inventory" />
          <p className="mt-1 text-sm text-muted-foreground">
            Unlisted verified assets across the exchange. Submit an offer on a SKU — every eligible
            seller is notified.
          </p>
        </div>
        <div className="shrink-0 text-right">
          <div className="font-mono text-lg font-semibold text-foreground">
            {unitsShown} <span className="text-xs font-normal text-muted-foreground">units</span>
          </div>
          <div className="text-xs text-muted-foreground">{skusShown} SKUs unlisted</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 mt-4 flex flex-wrap items-center gap-3">
        <div className="flex min-w-[220px] max-w-xs flex-1 items-center gap-2 rounded-md border border-border bg-card px-2.5">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 text-muted-foreground">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search SKU, model, or manufacturer…"
            className="w-full border-none bg-transparent py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
        </div>
        <select value={assetClass} onChange={(e) => setAssetClass(e.target.value)} className={selectClass}>
          <option value="">Any asset class</option>
          {(reference?.assetClasses ?? []).map((ac) => (
            <option key={ac.id} value={ac.id}>
              {ac.name}
            </option>
          ))}
        </select>
        <select value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} className={selectClass}>
          <option value="">Any manufacturer</option>
          {manufacturers.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <select value={condition} onChange={(e) => setCondition(e.target.value)} className={selectClass}>
          {CONDITION_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select value={regionId} onChange={(e) => setRegionId(e.target.value)} className={selectClass}>
          <option value="">Any region</option>
          {(reference?.regions ?? []).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </select>
      </div>

      <QueryBoundary query={catalogQ} errorTitle="Couldn't load inventory." empty={() => false}>
        {() => (
          <>
            {sections.length === 0 && (
              <div className="rounded-lg border border-border bg-card p-12 text-center">
                <div className="text-sm font-semibold text-foreground">No inventory matches your filters</div>
                <div className="mt-1 text-sm text-muted-foreground">Adjust your search or filters to see unlisted assets.</div>
              </div>
            )}

            {sections.map((sec) => (
        <details key={sec.assetClass} open className="group/sec mb-8">
          {/* Asset-class header — <details>/<summary> gives collapse for free */}
          <summary className="mb-3 flex cursor-pointer list-none items-baseline gap-3 [&::-webkit-details-marker]:hidden">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 self-center text-muted-foreground transition-transform group-open/sec:rotate-90">
              <path d="m9 18 6-6-6-6" />
            </svg>
            <h2 className="text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              {classById.get(sec.assetClass) ?? sec.assetClass}
            </h2>
            <div className="h-px flex-1 bg-muted" />
            <div className="font-mono text-[13px] text-muted-foreground">
              {sec.skuCount} SKU{sec.skuCount === 1 ? '' : 's'} &middot; {sec.totalUnits} unit
              {sec.totalUnits === 1 ? '' : 's'}
            </div>
          </summary>

          {sec.mfrs.map((m) => (
            <details key={m.make} open className="group/mfr mb-5 pl-5">
              {/* Manufacturer subheader */}
              <summary className="mb-2 flex cursor-pointer list-none items-baseline gap-3 [&::-webkit-details-marker]:hidden">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 self-center text-muted-foreground transition-transform group-open/mfr:rotate-90">
                  <path d="m9 18 6-6-6-6" />
                </svg>
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  <ManufacturerLogo id={m.manufacturerId} name={m.make} />
                </div>
                <div className="font-mono text-[13px] text-muted-foreground">
                  {m.groups.length} SKU{m.groups.length === 1 ? '' : 's'} &middot; {m.totalUnits} unit
                  {m.totalUnits === 1 ? '' : 's'}
                </div>
              </summary>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {m.groups.map((g) => (
              <div
                key={g.skuId}
                className="flex flex-col rounded-xl border border-border bg-card p-4 shadow-sm"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <div className="min-w-0 text-sm font-semibold text-foreground">{cardLabel(g)}</div>
                  <div className="whitespace-nowrap font-mono text-[13px] text-muted-foreground">
                    {g.totalUnits} unit{g.totalUnits === 1 ? '' : 's'}
                  </div>
                </div>

                <div className="mb-0.5 mt-2.5 flex items-baseline gap-2">
                  <div className="font-mono text-xl font-semibold text-foreground">{usd(g.mcmpUsd)}</div>
                </div>
                <div className="mb-2.5 text-xs text-muted-foreground">FMV median</div>

                {/* Condition mix bar */}
                <div className="mb-1.5 flex h-[5px] gap-0.5 overflow-hidden rounded-sm">
                  {BAND_ORDER.filter((k) => g.perBand[k]).map((k) => (
                    <div key={k} className={BAND_BAR[k]} style={{ flex: g.perBand[k] }} />
                  ))}
                </div>
                <div className="mb-3.5 font-mono text-[13px] text-muted-foreground">
                  {BAND_ORDER.filter((k) => g.perBand[k]).map((k) => `${conditionLabel(k)} ${g.perBand[k]}`).join(' · ')}
                </div>

                <button
                  type="button"
                  onClick={() => setDrawerSkuId(g.skuId)}
                  className="mt-auto w-full rounded-md border border-border px-4 py-1.5 text-xs font-semibold text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-brand-500"
                >
                  Submit Offer
                </button>
              </div>
                ))}
              </div>
            </details>
          ))}
        </details>
            ))}
          </>
        )}
      </QueryBoundary>

      {drawerGroup && (
        <OfferDrawer
          group={drawerGroup}
          skuLabel={drawerGroup.modelName}
          regionById={regionById}
          onClose={() => setDrawerSkuId(null)}
        />
      )}
    </div>
  );
}
